<script setup lang="ts">
import { ref } from "vue";

const props = defineProps<{
  photo: string | null;
  microsoftPhoto: string | null;
  photoChangedLocally: boolean;
  loading?: boolean;
}>();

const emit = defineEmits<{
  upload: [file: File];
  restore: [];
}>();

const fileInput = ref<HTMLInputElement | null>(null);

function openFilePicker(): void {
  fileInput.value?.click();
}

function togglePhoto(): void {
  if (props.photoChangedLocally && props.microsoftPhoto) {
    emit("restore");
  } else {
    openFilePicker();
  }
}

function onFileSelected(event: Event): void {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (file) {
    emit("upload", file);
    input.value = "";
  }
}
</script>

<template>
  <div class="profile-photo-section">
    <div class="profile-photo-container">
      <div
        class="profile-photo-wrapper"
        v-bind:class="{ 'no-photo': !photo }"
        v-on:click="openFilePicker"
      >
        <img
          v-if="photo"
          class="profile-photo-img"
          v-bind:src="photo"
          alt="Foto de perfil"
        />
        <v-icon
          v-if="!photo"
          class="profile-photo-placeholder"
          name="md-nophotography"
        />
      </div>
      <button
        class="profile-photo-action"
        v-bind:class="{ 'remove-mode': photoChangedLocally && microsoftPhoto }"
        v-bind:title="
          photoChangedLocally && microsoftPhoto
            ? 'Remover foto local e voltar à original'
            : 'Alterar foto'
        "
        type="button"
        v-on:click.stop="togglePhoto"
      >
        <v-icon v-bind:name="photoChangedLocally && microsoftPhoto ? 'md-close' : 'md-cameraalt-outlined'" />
      </button>
    </div>
    <input
      ref="fileInput"
      type="file"
      class="hidden"
      accept="image/*"
      v-on:change="onFileSelected"
    />
    <div v-if="loading" class="profile-loading">
      <div class="spinner"></div>
      <span>Carregando dados do Microsoft 365...</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.hidden {
  display: none;
}

.profile-photo-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 0;
}

.profile-photo-container {
  position: relative;
  display: inline-block;
  line-height: 0;
}

.profile-photo-wrapper {
  position: relative;
  width: 110px;
  height: 110px;
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
  border: 3px solid var(--colorBackground);
  box-shadow: var(--shadow4);
  transition: border-color var(--transition), box-shadow var(--transition), transform var(--transition);

  &:hover {
    box-shadow: 0 0 0 3px var(--colorBorderFocus), var(--shadow4);
    transform: translateY(-1px);
  }

  .profile-photo-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &.no-photo {
    background-color: var(--colorBackgroundSubtle);

    .profile-photo-img {
      opacity: 0;
    }
  }
}

.profile-photo-placeholder {
  position: absolute;
  inset: 0;
  width: 48px;
  height: 48px;
  margin: auto;
  color: var(--colorTextTertiary);
  opacity: 0.5;
}

.profile-photo-action {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid var(--colorBackground);
  background-color: var(--colorBackground);
  color: var(--colorText);
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  box-shadow: 0 1px 4px var(--shadowKey);
  transition: background-color var(--transition), color var(--transition), transform var(--transition);
  z-index: 2;

  .ov-icon {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background-color: var(--colorBackgroundSubtle);
    transform: scale(1.1);
  }

  &.remove-mode {
    background-color: var(--colorErrorBackground);
    color: var(--colorError);
    border-color: var(--colorErrorBackground);

    &:hover {
      background-color: var(--colorStatusDangerBackground2);
    }

    .ov-icon {
      color: var(--colorError);
    }
  }
}

.profile-loading {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-sm);
  font-size: var(--font-size-sm);
  color: var(--colorTextSecondary);
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid var(--colorBorder);
  border-top-color: var(--colorPrimary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
